import { Component, Input, Output, EventEmitter, ContentChild, TemplateRef, ElementRef, inject, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

// ==================== APP DROPDOWN COMPONENT (shared, generic) ====================
// Generic, template-driven dropdown: consumers supply their own #trigger and #option
// templates and get open/close, outside-click-to-close, and "active option" tracking for free.
@Component({
    selector: 'app-dropdown',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './dropdown.component.html',
    styleUrl: './dropdown.component.scss',
})
export class DropdownComponent<T = any> {
    @Input() options: readonly T[] = [];
    @Input() selected: T | null = null;
    // Identity function used to compare options (and compare against `selected`) instead of by reference —
    // lets consumers pass e.g. an id-based comparator for objects that aren't reference-equal.
    @Input() trackBy: (item: T) => unknown = item => item;
    // Named to pair with `selected` so consumers can use [(selected)]="..." two-way binding syntax
    @Output() selectedChange = new EventEmitter<T>();

    // Consumer-provided templates, projected via <ng-container *ngTemplateOutlet>
    @ContentChild('trigger') triggerTemplate?: TemplateRef<any>;
    @ContentChild('option') optionTemplate?: TemplateRef<any>;

    public isOpen = signal(false);
    private elementRef = inject(ElementRef);

    // Closes the panel when the user clicks anywhere outside this component
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.elementRef.nativeElement.contains(event.target)) {
            this.isOpen.set(false);
        }
    }

    // Opens/closes the panel; stopPropagation prevents this same click from
    // immediately re-triggering onDocumentClick and closing it again
    toggle(event?: Event): void {
        event?.stopPropagation();
        this.isOpen.update(open => !open);
    }

    select(option: T): void {
        this.selectedChange.emit(option);
        this.isOpen.set(false);
    }

    // Compares by trackBy identity rather than reference, so options that are
    // structurally equal but different object instances still highlight correctly
    isActive(option: T): boolean {
        return this.selected != null && this.trackBy(option) === this.trackBy(this.selected);
    }
}
